import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  createMediaShare,
  listMediaShares,
  deleteMediaShare,
  ShareType,
  SharePermission,
  MediaShare,
} from "../../api/mediaShareAPI";
import {
  getPrimaryText,
  getSecondaryText,
  getMutedText,
} from "../../utils/themeUtils";
import Tooltip from "./Tooltip";
import { Button } from "../ui";

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  // One of these identifies what we're sharing
  mediaId?: string;
  folderPath?: string;
  fileName?: string;
}

const SHARE_TYPE_LABELS: Record<ShareType, string> = {
  all_staff: "All Staff",
  members: "All Members (read-only)",
  public: "Public (anyone with link)",
  user: "Specific User",
};

const PERMISSION_LABELS: Record<SharePermission, string> = {
  view: "View only",
  download: "View + Download",
  edit: "View + Download + Edit",
};

const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  mediaId,
  folderPath,
  fileName,
}) => {
  const [shareType, setShareType] = useState<ShareType>("all_staff");
  const [permission, setPermission] = useState<SharePermission>("download");
  const [generateLink, setGenerateLink] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingShares, setExistingShares] = useState<MediaShare[]>([]);
  const [, setLoadingShares] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchExistingShares();
      setShareType("all_staff");
      setPermission("download");
      setGenerateLink(false);
      setNote("");
    }
  }, [isOpen, mediaId, folderPath]);

  const fetchExistingShares = async () => {
    setLoadingShares(true);
    try {
      const shares = await listMediaShares({
        media_id: mediaId,
        folder_path: folderPath,
      });
      setExistingShares(shares);
    } catch {
      // Non-fatal
    } finally {
      setLoadingShares(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await createMediaShare({
        media_id: mediaId,
        folder_path: folderPath,
        share_type: shareType,
        permission,
        note: note || undefined,
        generate_link: generateLink || shareType === "public",
      });
      toast.success("Shared successfully");
      await fetchExistingShares();
      setNote("");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to share");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await deleteMediaShare(id);
      setExistingShares((prev) => prev.filter((s) => s.id !== id));
      toast.success("Share revoked");
    } catch {
      toast.error("Failed to revoke share");
    }
  };

  const copyLink = (share: MediaShare) => {
    if (!share.share_token) return;
    const tenantSlug = window.location.pathname.split("/").filter(Boolean)[0];
    const link = `${window.location.origin}/${tenantSlug}/shared/${share.share_token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(share.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Link copied to clipboard");
  };

  if (!isOpen) return null;

  const bgClass = isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900";
  const borderClass = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputClass = `block w-full rounded-md px-3 py-2 text-sm border ${borderClass} ${
    isDarkMode ? "bg-gray-900" : "bg-white"
  }`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className={`${bgClass} rounded-lg shadow-xl w-full max-w-md border ${borderClass}`}>
        {/* Header */}
        <div className={`px-5 py-3 border-b ${borderClass} flex items-center justify-between`}>
          <div>
            <h2 className={`text-base font-semibold ${getPrimaryText(isDarkMode)}`}>
              Share {folderPath ? "Folder" : "File"}
            </h2>
            {fileName && (
              <p className={`text-xs mt-0.5 ${getMutedText(isDarkMode)} truncate max-w-[280px]`}>
                {fileName}
              </p>
            )}
          </div>
          <Button type="button" variant="neutral" size="sm" iconOnly aria-label="Close" onClick={onClose}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Create new share */}
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
              Share with
            </label>
            <select
              value={shareType}
              onChange={(e) => setShareType(e.target.value as ShareType)}
              className={inputClass}
            >
              {(Object.keys(SHARE_TYPE_LABELS) as ShareType[])
                // "user" needs a user picker that does not exist yet.
                //
                // "members" is hidden because it silently does nothing: no
                // member-facing surface calls listMediaShares, and the route
                // requires manage_media_shares, which no member holds. Picking
                // it produced a success toast and a listed row for a file no
                // member could ever open — unless the staffer also ticked
                // "generate shareable link", a separate box defaulted off.
                // The server-side member filter in listShares is left intact,
                // so restoring this option is a one-line change the day a
                // member media page ships.
                .filter((t) => t !== "user" && t !== "members")
                .map((t) => (
                  <option key={t} value={t}>
                    {SHARE_TYPE_LABELS[t]}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
              Permission
            </label>
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as SharePermission)}
              className={inputClass}
            >
              {(Object.keys(PERMISSION_LABELS) as SharePermission[]).map((p) => (
                <option key={p} value={p}>
                  {PERMISSION_LABELS[p]}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={generateLink || shareType === "public"}
              onChange={(e) => setGenerateLink(e.target.checked)}
              disabled={shareType === "public"}
              className="rounded"
            />
            <span className={`text-xs ${getSecondaryText(isDarkMode)}`}>
              Generate shareable link
            </span>
          </label>

          <input
            aria-label="Add a note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)"
            className={inputClass}
          />

          <button
            onClick={handleCreate}
            disabled={loading}
            className="w-full px-3 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {loading ? "Sharing..." : "Share"}
          </button>
        </div>

        {/* Existing shares */}
        {existingShares.length > 0 && (
          <div className={`px-5 py-3 border-t ${borderClass}`}>
            <h3 className={`text-xs font-semibold mb-2 ${getSecondaryText(isDarkMode)}`}>
              Current shares
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {existingShares.map((share) => (
                <div
                  key={share.id}
                  className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                    isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <span className={`font-medium ${getPrimaryText(isDarkMode)}`}>
                      {SHARE_TYPE_LABELS[share.share_type]}
                    </span>
                    <span className={`ml-2 ${getMutedText(isDarkMode)}`}>
                      ({PERMISSION_LABELS[share.permission]})
                    </span>
                    {share.shared_by_name && (
                      <span className={`block ${getMutedText(isDarkMode)}`}>
                        by {share.shared_by_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {share.share_token && (
                      <Tooltip content="Copy link"><button aria-label="Copy link"
                        onClick={() => copyLink(share)}
                        className="text-blue-600 dark:text-blue-400 hover:underline"

                      >
                        {copiedId === share.id ? "Copied!" : "Copy link"}
                      </button></Tooltip>
                    )}
                    <Tooltip content="Revoke"><Button
  type="button"
  variant="dangerGhost"
  size="sm"
  onClick={() => handleRevoke(share.id)}
  className="ml-2"
>
                      Revoke
                    </Button></Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareDialog;
